/**
 * Y GO Shuttle Services France - Page Flotte REFONTE
 * Objectif : Valoriser le standing premium - Expliquer la philosophie de sélection
 * Ton : Premium, élégant, rassurant
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, Users, Luggage, Shield, Star, Check, Calendar, Phone, Mail,
  Loader2, Wifi, Droplets, Baby, Sparkles, Settings, Globe,
  CheckCircle2, Wind, Gauge, Award, Network
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Header, Footer } from '../components/layout';
import { useCMSPage } from '../hooks/useCMSPage';
import SafeImage from '../components/SafeImage';

const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1764090317623-06968349ad23?auto=format&fit=crop&w=1600&q=80';

export default function FlottePage() {
  const { content: cmsContent, loading: cmsLoading } = useCMSPage('flotte');

  // Get hero data from CMS
  const banner = cmsContent?.banner || {};
  const heroImage = banner.image_url || DEFAULT_HERO_IMAGE;
  const heroTitle = banner.title || 'Notre flotte de véhicules premium';
  const heroSubtitle = banner.subtitle || 'Confort • Élégance • Exigence';

  // SEO
  useEffect(() => {
    const meta = cmsContent?.meta || {};
    document.title = meta.title || 'Notre Flotte Premium | Y GO Shuttle Services';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = meta.description || 'Flotte de véhicules premium Y GO : Mercedes Classe E et V, Tesla, VW Caravelle. Berlines et vans haut de gamme pour transferts VTC au Havre.';
    
    return () => {
      document.title = 'Y GO Shuttle Services';
    };
  }, [cmsContent]);

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050509]">
      <Header activePage="Flotte" />
      
      <main>
        {/* HEADER COMPACT */}
        <section className="relative h-[450px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <SafeImage 
              src={heroImage}
              fallbackSrc={DEFAULT_HERO_IMAGE}
              alt="Notre flotte premium"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, rgba(11,11,11,0.9) 0%, rgba(11,11,11,0.7) 40%, rgba(11,11,11,0) 80%)'
              }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="max-w-3xl space-y-4">
              <p className="text-[10px] font-medium tracking-[0.35em] uppercase text-[#C9A24D]">
                Véhicules premium sélectionnés
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#E5E5E5] leading-tight" style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}>
                Notre <span className="text-[#C9A24D]">flotte</span> de véhicules premium
              </h1>
              <p className="text-[#E5E5E5]/70 text-lg">
                Confort • Élégance • Exigence
              </p>
              <p className="text-[#E5E5E5]/60 text-base max-w-2xl">
                Chaque véhicule est sélectionné pour garantir un voyage impeccable
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 1 : PHILOSOPHIE DE LA FLOTTE */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Notre philosophie de <span className="text-[#C9A24D]">sélection</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Au-delà des marques, c'est l'expérience de voyage qui guide nos choix
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {[
                { 
                  title: 'Véhicules récents',
                  desc: 'Flotte renouvelée régulièrement, aucun véhicule de plus de 3 ans',
                  icon: Award
                },
                { 
                  title: 'Entretien rigoureux',
                  desc: 'Révisions constructeur respectées, nettoyage complet après chaque course',
                  icon: Settings
                },
                { 
                  title: 'Équipements premium',
                  desc: 'Climatisation, WiFi, prises USB, sièges cuir, insonorisation',
                  icon: Star
                },
                { 
                  title: 'Standards élevés',
                  desc: 'Même niveau d\'exigence pour tous nos véhicules et ceux de nos partenaires',
                  icon: Shield
                }
              ].map((principe) => (
                <div key={principe.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 text-center backdrop-blur-sm">
                  <principe.icon className="w-8 h-8 text-[#C9A24D] mx-auto mb-3" />
                  <h3 className="text-[#F5F5F5] font-semibold mb-2 text-sm">{principe.title}</h3>
                  <p className="text-[#A7A7A7] text-xs leading-relaxed">{principe.desc}</p>
                </div>
              ))}
            </div>

            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm border-l-4 border-[#C9A24D]">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#C9A24D] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-[#F5F5F5] font-semibold mb-2">Notre engagement qualité</h4>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    Nous ne possédons pas la plus grande flotte, mais <strong className="text-[#F5F5F5]">la plus cohérente</strong>. Chaque véhicule, qu'il soit exploité directement par Y GO ou via nos partenaires certifiés, respecte nos <strong className="text-[#C9A24D]">critères de sélection stricts</strong> : âge maximum 3 ans, marques premium uniquement, équipements complets, présentation impeccable.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 : BERLINES PREMIUM */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Berlines <span className="text-[#C9A24D]">Premium</span>
              </h2>
              <p className="text-[#A7A7A7] text-base">
                Idéales pour déplacements individuels, business et transferts
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Mercedes Classe E */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] backdrop-blur-sm">
                <div className="h-48 bg-[#0B0B0B]/50 flex items-center justify-center">
                  <Car className="w-20 h-20 text-[#C9A24D]/30" />
                </div>
                <div className="p-8">
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#F5F5F5] mb-2">
                    Mercedes Classe E
                  </h3>
                  <p className="text-[#C9A24D] text-sm mb-4">Berline executive</p>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed mb-6">
                    Référence du segment berline premium. Confort de conduite exceptionnel, insonorisation optimale, finitions haut de gamme. Idéale pour trajets business et transferts aéroports.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#0B0B0B]/50 p-3 text-center">
                      <Users className="w-5 h-5 text-[#C9A24D] mx-auto mb-1" />
                      <p className="text-[#F5F5F5] text-sm font-semibold">3 passagers</p>
                      <p className="text-[#A7A7A7] text-xs">max</p>
                    </div>
                    <div className="bg-[#0B0B0B]/50 p-3 text-center">
                      <Luggage className="w-5 h-5 text-[#C9A24D] mx-auto mb-1" />
                      <p className="text-[#F5F5F5] text-sm font-semibold">3 valises</p>
                      <p className="text-[#A7A7A7] text-xs">+ bagages cabine</p>
                    </div>
                  </div>

                  <div className="border-t border-[#C9A24D]/20 pt-4">
                    <p className="text-[#C9A24D] text-xs uppercase tracking-wider mb-3">Équipements</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['Cuir', 'Climatisation auto', 'WiFi gratuit', 'Prises USB', 'Sièges chauffants', 'Insonorisation'].map((eq, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#C9A24D]" />
                          <span className="text-[#D4D4D4] text-xs">{eq}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tesla Model Y ou équivalent */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] backdrop-blur-sm">
                <div className="h-48 bg-[#0B0B0B]/50 flex items-center justify-center">
                  <Wind className="w-20 h-20 text-[#C9A24D]/30" />
                </div>
                <div className="p-8">
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#F5F5F5] mb-2">
                    Tesla Model Y
                  </h3>
                  <p className="text-[#C9A24D] text-sm mb-4">SUV électrique premium</p>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed mb-6">
                    Alliant innovation technologique et confort, notre offre électrique répond aux attentes des clients sensibles à l'environnement sans compromis sur le standing.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#0B0B0B]/50 p-3 text-center">
                      <Users className="w-5 h-5 text-[#C9A24D] mx-auto mb-1" />
                      <p className="text-[#F5F5F5] text-sm font-semibold">4 passagers</p>
                      <p className="text-[#A7A7A7] text-xs">confortables</p>
                    </div>
                    <div className="bg-[#0B0B0B]/50 p-3 text-center">
                      <Luggage className="w-5 h-5 text-[#C9A24D] mx-auto mb-1" />
                      <p className="text-[#F5F5F5] text-sm font-semibold">4 valises</p>
                      <p className="text-[#A7A7A7] text-xs">+ bagages cabine</p>
                    </div>
                  </div>

                  <div className="border-t border-[#C9A24D]/20 pt-4">
                    <p className="text-[#C9A24D] text-xs uppercase tracking-wider mb-3">Équipements</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['100% électrique', 'Autopilot Tesla', 'WiFi intégré', 'Écran tactile', 'Son premium', 'Silencieux'].map((eq, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#C9A24D]" />
                          <span className="text-[#D4D4D4] text-xs">{eq}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3 : VANS & GRANDE CAPACITÉ */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Vans & véhicules <span className="text-[#C9A24D]">grande capacité</span>
              </h2>
              <p className="text-[#A7A7A7] text-base">
                Pour groupes, familles et excursions en tout confort
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Mercedes Classe V */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] backdrop-blur-sm">
                <div className="h-48 bg-[#0B0B0B]/50 flex items-center justify-center">
                  <Car className="w-24 h-24 text-[#C9A24D]/30" />
                </div>
                <div className="p-8">
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#F5F5F5] mb-2">
                    Mercedes Classe V
                  </h3>
                  <p className="text-[#C9A24D] text-sm mb-4">Van luxe modulable</p>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed mb-6">
                    Le van premium par excellence. Espace généreux, sièges individuels confortables, modularité totale. Parfait pour familles nombreuses, groupes d'affaires et excursions longue distance.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#0B0B0B]/50 p-3 text-center">
                      <Users className="w-5 h-5 text-[#C9A24D] mx-auto mb-1" />
                      <p className="text-[#F5F5F5] text-sm font-semibold">7 passagers</p>
                      <p className="text-[#A7A7A7] text-xs">sièges individuels</p>
                    </div>
                    <div className="bg-[#0B0B0B]/50 p-3 text-center">
                      <Luggage className="w-5 h-5 text-[#C9A24D] mx-auto mb-1" />
                      <p className="text-[#F5F5F5] text-sm font-semibold">7 valises</p>
                      <p className="text-[#A7A7A7] text-xs">+ bagages cabine</p>
                    </div>
                  </div>

                  <div className="border-t border-[#C9A24D]/20 pt-4">
                    <p className="text-[#C9A24D] text-xs uppercase tracking-wider mb-3">Équipements</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['Cuir intégral', 'Clim 3 zones', 'WiFi gratuit', 'Tables escamotables', 'Rideaux opaques', 'Confort longue distance'].map((eq, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#C9A24D]" />
                          <span className="text-[#D4D4D4] text-xs">{eq}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* VW Caravelle */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] backdrop-blur-sm">
                <div className="h-48 bg-[#0B0B0B]/50 flex items-center justify-center">
                  <Users className="w-20 h-20 text-[#C9A24D]/30" />
                </div>
                <div className="p-8">
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#F5F5F5] mb-2">
                    VW Caravelle
                  </h3>
                  <p className="text-[#C9A24D] text-sm mb-4">Minibus confort</p>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed mb-6">
                    Solution optimale pour groupes professionnels ou familiaux. Volume généreux, accès facile, polyvalence. Particulièrement adapté aux excursions et transferts collectifs.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#0B0B0B]/50 p-3 text-center">
                      <Users className="w-5 h-5 text-[#C9A24D] mx-auto mb-1" />
                      <p className="text-[#F5F5F5] text-sm font-semibold">8 passagers</p>
                      <p className="text-[#A7A7A7] text-xs">configuration standard</p>
                    </div>
                    <div className="bg-[#0B0B0B]/50 p-3 text-center">
                      <Luggage className="w-5 h-5 text-[#C9A24D] mx-auto mb-1" />
                      <p className="text-[#F5F5F5] text-sm font-semibold">8 bagages</p>
                      <p className="text-[#A7A7A7] text-xs">modulable</p>
                    </div>
                  </div>

                  <div className="border-t border-[#C9A24D]/20 pt-4">
                    <p className="text-[#C9A24D] text-xs uppercase tracking-wider mb-3">Équipements</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['Climatisation', 'Sièges confort', 'Grand volume', 'Accès facile', 'Portes latérales', 'Configuration modulable'].map((eq, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-[#C9A24D]" />
                          <span className="text-[#D4D4D4] text-xs">{eq}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage recommandé */}
            <div className="mt-8 relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
              <h4 className="text-[#C9A24D] text-sm uppercase tracking-wider mb-4">Usage recommandé par type de véhicule</h4>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { type: 'Berline', usages: ['1-3 passagers', 'Trajets business', 'Transferts aéroports', 'Discrétion & élégance'] },
                  { type: 'Van 7 places', usages: ['4-7 passagers', 'Familles', 'Groupes d\'affaires', 'Excursions confort'] },
                  { type: 'Minibus 8 places', usages: ['6-8 passagers', 'Excursions groupe', 'Événements', 'Navettes'] }
                ].map((cat) => (
                  <div key={cat.type} className="bg-[#0B0B0B]/40 p-4">
                    <p className="text-[#F5F5F5] font-semibold mb-3 text-sm">{cat.type}</p>
                    <div className="space-y-1">
                      {cat.usages.map((usage, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-1 h-1 bg-[#C9A24D] rounded-full" />
                          <span className="text-[#A7A7A7] text-xs">{usage}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 : ADAPTATION BESOINS SPÉCIFIQUES */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                La flotte s'adapte au <span className="text-[#C9A24D]">service</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Pas l'inverse — nous choisissons le véhicule optimal pour chaque besoin
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Trajets longue distance',
                  desc: 'Véhicules sélectionnés pour leur confort sur autoroute, insonorisation, sièges ergonomiques',
                  vehicule: 'Mercedes Classe E/V privilégiées',
                  icon: Gauge
                },
                {
                  title: 'Événements & cérémonies',
                  desc: 'Véhicules adaptables, décorables, avec discrétion et élégance pour vos moments importants',
                  vehicule: 'Berline premium ou van selon groupe',
                  icon: Star
                },
                {
                  title: 'Besoins spécifiques',
                  desc: 'Volume exceptionnel de bagages, matériel professionnel, équipement médical sur demande',
                  vehicule: 'Configuration sur mesure',
                  icon: Settings
                }
              ].map((cas) => (
                <div key={cas.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <cas.icon className="w-8 h-8 text-[#C9A24D] mb-4" />
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] mb-2">
                    {cas.title}
                  </h3>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed mb-3">{cas.desc}</p>
                  <p className="text-[#C9A24D] text-xs font-semibold">{cas.vehicule}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm border-l-4 border-[#C9A24D]">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#C9A24D] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-[#F5F5F5] font-semibold mb-2">Flexibilité totale</h4>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    Besoin de 3 berlines coordonnées pour un événement d'entreprise ? D'un van avec sièges enfants pour une famille de 6 ? D'un véhicule adapté PMR ? <strong className="text-[#C9A24D]">Nous trouvons toujours la solution</strong>, en mobilisant notre flotte propre ou celle de nos partenaires selon la disponibilité et le besoin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5 : QUALITÉ CONSTANTE AVEC PARTENAIRES */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Qualité constante, même avec <span className="text-[#C9A24D]">partenaires</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Standards Y GO imposés sur l'ensemble du réseau
              </p>
            </div>

            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm mb-8">
              <div className="flex items-start gap-4 mb-6">
                <Globe className="w-10 h-10 text-[#C9A24D] flex-shrink-0" />
                <div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5] mb-2">
                    Standards Y GO pour véhicules partenaires
                  </h3>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    Lorsque nous coordonnons un trajet via notre réseau de partenaires (notamment pour les longs trajets ou destinations hors Normandie), <strong className="text-[#F5F5F5]">nous imposons les mêmes critères de qualité</strong> que pour notre flotte propre. Vous bénéficiez d'une <strong className="text-[#C9A24D]">expérience homogène</strong>, quel que soit le chauffeur.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-[#C9A24D] text-sm uppercase tracking-wider mb-4">Critères véhicules partenaires</h4>
                  <div className="space-y-2">
                    {[
                      'Âge maximum 3 ans',
                      'Marques premium uniquement (Mercedes, Tesla, Audi, BMW, Volvo)',
                      'Entretien constructeur à jour',
                      'Équipements complets (WiFi, clim, USB)',
                      'Présentation intérieure/extérieure impeccable',
                      'Assurance professionnelle tous risques'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-[#C9A24D] text-sm uppercase tracking-wider mb-4">Contrôle qualité</h4>
                  <div className="space-y-2">
                    {[
                      'Audit annuel de chaque partenaire',
                      'Contrôle photo avant chaque mise à disposition',
                      'Feedback client systématique',
                      'Déréférencement si note < 4.5/5',
                      'Vérification assurance et permis mensuelle',
                      'Formation continue aux standards Y GO'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 : CONFORT & SÉCURITÉ */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Confort & <span className="text-[#C9A24D]">Sécurité</span>
              </h2>
              <p className="text-[#A7A7A7] text-base">
                Au cœur de notre promesse de service
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Confort */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center">
                    <Star className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5]">
                    Confort à bord
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Wifi, label: 'WiFi gratuit', desc: 'Connexion haut débit' },
                    { icon: Droplets, label: 'Eau minérale', desc: 'Offerte' },
                    { icon: Wind, label: 'Climatisation', desc: 'Réglage individuel' },
                    { icon: Baby, label: 'Sièges enfants', desc: 'Gratuits sur demande' },
                    { icon: Sparkles, label: 'Propreté', desc: 'Nettoyage après chaque course' },
                    { icon: Settings, label: 'USB & prises', desc: 'Chargeurs disponibles' }
                  ].map((item) => (
                    <div key={item.label} className="bg-[#0B0B0B]/40 p-3 text-center">
                      <item.icon className="w-5 h-5 text-[#C9A24D] mx-auto mb-2" />
                      <p className="text-[#F5F5F5] text-xs font-semibold mb-0.5">{item.label}</p>
                      <p className="text-[#A7A7A7] text-xs">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sécurité */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5]">
                    Sécurité & discrétion
                  </h3>
                </div>
                <div className="space-y-3">
                  {[
                    'Contrôle technique rigoureux tous les 6 mois',
                    'Assurance tous risques avec garantie passagers',
                    'Équipements de sécurité aux normes (airbags, ABS, ESP)',
                    'Maintenance préventive stricte',
                    'Chauffeurs formés conduite défensive',
                    'Vitres teintées pour discrétion (sur demande)',
                    'GPS et assistance dépannage 24/7',
                    'Désinfection quotidienne des habitacles'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                      <span className="text-[#D4D4D4] text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Présentation chauffeurs */}
            <div className="mt-8 relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm border-l-4 border-[#C9A24D]">
              <div className="flex items-start gap-4">
                <Users className="w-6 h-6 text-[#C9A24D] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-[#F5F5F5] font-semibold mb-2">Présentation chauffeurs</h4>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    Au-delà du véhicule, c'est <strong className="text-[#F5F5F5]">la présentation de nos chauffeurs</strong> qui fait la différence. Tenue professionnelle (costume ou polo Y GO selon contexte), courtoisie, discrétion. Tous sont <strong>bilingues français/anglais</strong>, plusieurs parlent allemand, espagnol ou italien. Formation continue aux standards de service premium.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* GARANTIES FLOTTE */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Nos <span className="text-[#C9A24D]">garanties</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Véhicule garanti',
                  desc: 'Si un véhicule devient indisponible (panne, accident), un véhicule de standing équivalent ou supérieur est fourni sans délai ni surcoût',
                  icon: Shield
                },
                {
                  title: 'Propreté garantie',
                  desc: 'Chaque véhicule est nettoyé intégralement après chaque course. En cas de problème, signalement immédiat et véhicule de remplacement',
                  icon: Sparkles
                },
                {
                  title: 'Équipement garanti',
                  desc: 'WiFi, eau, climatisation fonctionnels à chaque trajet. Réclamation possible si manquant',
                  icon: CheckCircle2
                }
              ].map((garantie) => (
                <div key={garantie.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <garantie.icon className="w-8 h-8 text-[#C9A24D] mx-auto mb-4" />
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] mb-3 text-center">
                    {garantie.title}
                  </h3>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed text-center">{garantie.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL SOBRE */}
        <section className="py-20 bg-gradient-to-r from-[#111111] to-[#1F1F1F] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
              Choisissez votre <span className="text-[#C9A24D]">véhicule</span>
            </h2>
            <p className="text-[#A7A7A7] text-base mb-8 max-w-2xl mx-auto">
              Réservez en ligne ou contactez-nous pour un conseil personnalisé
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
              <Link to="/booking">
                <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-10 py-4 rounded-none text-base">
                  <Calendar className="w-5 h-5 mr-2" />
                  Réserver un trajet
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-10 py-4 rounded-none text-base"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Conseil véhicule
                </Button>
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <a 
                href="tel:+33278780404"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Phone className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  Assistance 24/7
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Conseil immédiat</p>
              </a>
              <a 
                href="mailto:booking@y-go.fr"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Mail className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  Demande spécifique
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Réponse sous 2h</p>
              </a>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
